
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Task } from '@/types/lead';
import { completeTask, deleteTask, updateTask, createNextRecurrence } from '@/services/leads/taskService';
import { useLoading } from '@/context/LoadingContext';

export const useTaskMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { startLoading, stopLoading } = useLoading();

  const completeMutation = useCallback(async (taskId: string) => {
    const operationId = `complete-task-${taskId}`;
    startLoading(operationId, 'Completing task...');
    
    try {
      const task = await completeTask(taskId);
      
      // Auto-create next recurrence if applicable
      if (task.recurrencePattern) {
        const nextTask = await createNextRecurrence(task);
        if (nextTask) {
          toast({
            title: 'Recurring Task Created',
            description: `Next "${nextTask.title}" scheduled for ${nextTask.dueDate ? new Date(nextTask.dueDate).toLocaleDateString() : 'soon'}`,
          });
        }
      }
      
      toast({
        title: 'Task Completed',
        description: `"${task.title}" marked as completed`,
        variant: 'default',
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      return task;
    } catch (error) {
      toast({
        title: 'Error',
        description: (error as Error).message,
        variant: 'destructive',
      });
      throw error;
    } finally {
      stopLoading(operationId);
    }
  }, [queryClient, toast, startLoading, stopLoading]);

  const deleteMutation = useCallback(async (taskId: string) => {
    const operationId = `delete-task-${taskId}`;
    startLoading(operationId, 'Deleting task...');
    
    try {
      await deleteTask(taskId);
      toast({
        title: 'Success',
        description: 'Task deleted successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } catch (error) {
      toast({
        title: 'Error',
        description: (error as Error).message,
        variant: 'destructive',
      });
    } finally {
      stopLoading(operationId);
    }
  }, [queryClient, toast, startLoading, stopLoading]);

  const updateMutation = useCallback(async (taskId: string, updates: Partial<Task>) => {
    const operationId = `update-task-${taskId}`;
    startLoading(operationId, 'Updating task...');
    
    try {
      const task = await updateTask(taskId, updates);
      toast({
        title: 'Success',
        description: 'Task updated successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      return task;
    } catch (error) {
      toast({
        title: 'Error',
        description: (error as Error).message,
        variant: 'destructive',
      });
      throw error;
    } finally {
      stopLoading(operationId);
    }
  }, [queryClient, toast, startLoading, stopLoading]);

  const bulkCompleteMutation = useCallback(async (taskIds: string[]) => {
    const operationId = `bulk-complete-tasks`;
    startLoading(operationId, `Completing ${taskIds.length} tasks...`);
    
    try {
      const { error } = await (await import('@/integrations/supabase/client')).supabase
        .from('tasks')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .in('id', taskIds);
      
      if (error) throw error;
      
      toast({
        title: 'Tasks Completed',
        description: `${taskIds.length} tasks marked as completed`,
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } catch (error) {
      toast({
        title: 'Error',
        description: (error as Error).message,
        variant: 'destructive',
      });
    } finally {
      stopLoading(operationId);
    }
  }, [queryClient, toast, startLoading, stopLoading]);

  const bulkDeleteMutation = useCallback(async (taskIds: string[]) => {
    const operationId = `bulk-delete-tasks`;
    startLoading(operationId, `Deleting ${taskIds.length} tasks...`);
    
    try {
      const { error } = await (await import('@/integrations/supabase/client')).supabase
        .from('tasks')
        .delete()
        .in('id', taskIds);
      
      if (error) throw error;
      
      toast({
        title: 'Tasks Deleted',
        description: `${taskIds.length} tasks deleted successfully`,
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } catch (error) {
      toast({
        title: 'Error',
        description: (error as Error).message,
        variant: 'destructive',
      });
    } finally {
      stopLoading(operationId);
    }
  }, [queryClient, toast, startLoading, stopLoading]);

  const bulkReassignMutation = useCallback(async (taskIds: string[], assignedTo: string) => {
    const operationId = `bulk-reassign-tasks`;
    startLoading(operationId, `Reassigning ${taskIds.length} tasks...`);
    
    try {
      const { error } = await (await import('@/integrations/supabase/client')).supabase
        .from('tasks')
        .update({ assigned_to: assignedTo })
        .in('id', taskIds);
      
      if (error) throw error;
      
      toast({
        title: 'Tasks Reassigned',
        description: `${taskIds.length} tasks reassigned successfully`,
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } catch (error) {
      toast({
        title: 'Error',
        description: (error as Error).message,
        variant: 'destructive',
      });
    } finally {
      stopLoading(operationId);
    }
  }, [queryClient, toast, startLoading, stopLoading]);

  return {
    completeMutation,
    deleteMutation,
    updateMutation,
    bulkCompleteMutation,
    bulkDeleteMutation,
    bulkReassignMutation,
  };
};
